import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { format, isValid, parse, parseISO } from 'date-fns';
import { Formik } from 'formik';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentViewPropertySession } from 'src/hooks/use-current-view-property-session';
import { useViewer } from 'src/hooks/use-viewer';
import * as yup from 'yup';
import parseValueToDate from '~components/date-picker/helpers/parse-value-to-date';
import { DATE_FORMAT, SPLITTER } from '~components/date-picker/range-day-picker-input';
import { MUTATION_CREATE_BOOKING, MUTATION_EDIT_BOOKING } from '~containers/booking/gql';
import { BOOKING_CATEGORY, PAY_TO_TYPE_OPTIONS } from '~graphql-types';
import { useAcademicYearListOnFormikSelect } from '~utils/use-academic-year';
import useDisplayResponseErrorToast from '~utils/use-display-response-error-toast';
import useDisplayToast from '~utils/use-display-toast';
import BookingForm from './booking-form';
import { TENANCY_CUSTOMIZED } from './constants';

const GET_ACADEMIC_YEARS = gql`
  query GetAcademicYears($propertyId: ID!, $status: AcademicYearStatus) {
    getAcademicYears(propertyId: $propertyId, status: $status) {
      id
      isCurrent
      isEnabled
      name
    }
  }
`;

const formatAPIDate = (date) => format(parseValueToDate(date, DATE_FORMAT), 'yyyy-MM-dd');

const formatRangeDate = (start, end) =>
  `${format(start, DATE_FORMAT)}${SPLITTER}${format(end, DATE_FORMAT)}`;

function StepNewBooking(props) {
  const { t } = useTranslation('booking');
  const bookingFormRef = useRef();
  const displayToast = useDisplayToast();
  const displayResponseErrorToast = useDisplayResponseErrorToast();
  const [{ currentViewProperty }] = useCurrentViewPropertySession();
  const {
    currentRole: {
      accessScope: { properties },
    },
  } = useViewer();
  const { booking, student, onBookingCreated } = props;
  const edit = !!booking;
  const bookingCategory = booking?.category;

  const showNoPriceRoomTypes =
    booking?.nominationPlan?.salesPlanTemplate?.studentPayTo === PAY_TO_TYPE_OPTIONS.PARTNER;

  const defaultProperty = useMemo(
    () => properties.find(({ id }) => id === booking?.property.id) ?? currentViewProperty,
    [booking?.property.id, currentViewProperty, properties]
  );
  const [selectProperty, setSelectProperty] = useState(defaultProperty);

  const formatAcademicYears = (academicYearList) =>
    academicYearList.map((item) => ({ ...item, disabled: item.enabled === 'false' }));

  const academicYearListOnFormikSelect = useAcademicYearListOnFormikSelect();
  const getAcademicYearList = useMemo(() => formatAcademicYears(academicYearListOnFormikSelect), [
    academicYearListOnFormikSelect,
  ]);

  const academicYearListFromCurrentViewed = useMemo(() => getAcademicYearList, [
    getAcademicYearList,
  ]);
  const [academicYearList, setAcademicYearList] = useState(academicYearListFromCurrentViewed);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        academic_year: yup.string().label(t('booking.creating.form.academic_year')).required(),
        tenancy_option: yup.string().label(t('booking.creating.form.tenancy_option')).required(),
        room_type: yup.string().label(t('booking.creating.form.room_type')).required(),
        date_range: yup
          .string()
          .label(t('booking.creating.form.date_range'))
          .required()
          .test(
            'matchDateFormat',
            t('form.validation.validation_error', {
              fieldName: t('booking.creating.form.date_range'),
            }),
            function testDateRange(value) {
              if (value) {
                const [startDate, endDate] = value.split(SPLITTER);
                const inputDateFormat = DATE_FORMAT;
                return (
                  isValid(parse(startDate, inputDateFormat, new Date())) &&
                  isValid(parse(endDate, inputDateFormat, new Date()))
                );
              }

              return false;
            }
          ),
        free_cancellation_deadline_date: yup
          .string()
          .label(t('booking.creating.form.free_cancellation_deadline_date')),
        interval_for_turnaround: yup
          .number()
          .min(0)
          .label(t('booking.creating.form.interval_for_turnaround')),
      }),
    [t]
  );

  const initialValues = useMemo(
    () => ({
      academic_year:
        bookingCategory === BOOKING_CATEGORY.NOMINATION
          ? booking?.nominationPlan?.academicYear.id
          : booking?.academicYear.id || '',
      tenancy_option: booking?.tenancyOption.tenancyOptionId || '',
      room_type: booking?.roomType.roomTypeId,
      date_range:
        booking?.tenancyOption.startDate && booking?.tenancyOption.endDate
          ? formatRangeDate(
              parseISO(booking.tenancyOption.startDate),
              parseISO(booking.tenancyOption.endDate)
            )
          : '',
      free_cancellation_deadline_date: booking?.rateFlexibility?.freeCancellationDeadlineDate
        ? format(parseISO(booking.rateFlexibility.freeCancellationDeadlineDate), DATE_FORMAT)
        : '',
      interval_for_turnaround: booking?.tenancyOption?.intervalForTurnaround,
      isDifferentChargingDate: !!booking?.isDifferentChargingDate,
      charging_date_range:
        booking?.chargingStartDate && booking?.chargingEndDate
          ? formatRangeDate(
              parseISO(booking?.chargingStartDate),
              parseISO(booking?.chargingEndDate)
            )
          : '',
    }),
    [
      booking?.academicYear.id,
      booking?.chargingEndDate,
      booking?.chargingStartDate,
      booking?.isDifferentChargingDate,
      booking?.nominationPlan?.academicYear.id,
      booking?.rateFlexibility?.freeCancellationDeadlineDate,
      booking?.roomType.roomTypeId,
      booking?.tenancyOption.endDate,
      booking?.tenancyOption?.intervalForTurnaround,
      booking?.tenancyOption.startDate,
      booking?.tenancyOption.tenancyOptionId,
      bookingCategory,
    ]
  );

  const [submitBooking] = useMutation(edit ? MUTATION_EDIT_BOOKING : MUTATION_CREATE_BOOKING);

  const onSubmit = async (fields) => {
    const currentTenancyOption = bookingFormRef.current.tenancyOptions.tenancyOptions.find(
      (item) => item.id === fields?.tenancy_option
    );

    const [startDate, endDate] = fields.date_range.split(SPLITTER);
    const input = {
      academicYearId: fields.academic_year,
      tenancyStartDate: formatAPIDate(startDate),
      tenancyEndDate: formatAPIDate(endDate),
      roomTypeId: fields.room_type,
      isDifferentChargingDate: false,
    };
    if (fields.tenancy_option === TENANCY_CUSTOMIZED) {
      input.freeCancellationDeadlineDate = formatAPIDate(fields.free_cancellation_deadline_date);
      input.intervalForTurnaround = fields.interval_for_turnaround;
      input.tenancyOptionRateType = fields.rate_type;
      if (!!fields.isDifferentChargingDate) {
        input.isDifferentChargingDate = true;
        const [startDate, endDate] = fields.charging_date_range.split(SPLITTER);
        input.chargingStartDate = formatAPIDate(startDate);
        input.chargingEndDate = formatAPIDate(endDate);
      }
    } else {
      input.tenancyOptionId = fields.tenancy_option;
      input.tenancyOptionRateType = currentTenancyOption?.rateType;
      input.isDifferentChargingDate = !!currentTenancyOption?.isDifferentChargingDate;
    }

    if (edit) {
      input.id = booking.id;
    } else {
      input.studentId = student?.id;
    }

    try {
      const { data } = await submitBooking({ variables: { propertyId: selectProperty.id, input } });
      displayToast('success-updated', t('booking.creating.title'));
      if (onBookingCreated) onBookingCreated(data[edit ? 'editBooking' : 'createBooking']);
    } catch (error) {
      displayResponseErrorToast(error);
    }
  };
  const [queryGetAcademicYears, { data, loading: getAcademicYearsLoading }] = useLazyQuery(
    GET_ACADEMIC_YEARS,
    {
      onError: displayResponseErrorToast,
    }
  );

  useEffect(() => {
    if (data?.getAcademicYears) {
      const list = data.getAcademicYears.map((item) => ({
        value: item.id,
        children: item.name,
        label: item.name,
        enabled: item.isEnabled.toString(),
      }));
      setAcademicYearList(formatAcademicYears(list));
    }
  }, [data?.getAcademicYears]);

  const handleChangeProperty = (e) => {
    setSelectProperty(e);
    const propertyId = e.id;
    queryGetAcademicYears({ variables: { propertyId, status: 'CURRENT' } });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur={false}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <BookingForm
          academicYearList={academicYearList}
          bookingCategory={bookingCategory}
          currentProperty={selectProperty}
          edit={edit}
          formik={formik}
          ref={bookingFormRef}
          getAcademicYearsLoading={getAcademicYearsLoading}
          nominationPlanId={booking?.nominationPlan?.id}
          isPayToPartner={
            booking?.nominationPlan?.salesPlanTemplate?.studentPayTo === PAY_TO_TYPE_OPTIONS.PARTNER
          }
          student={student}
          showNoPriceRoomTypes={showNoPriceRoomTypes}
          onPropertyChange={(e) => {
            if (e.id !== selectProperty.id) {
              formik.resetForm();
              handleChangeProperty(e);
            }
          }}
        />
      )}
    </Formik>
  );
}

StepNewBooking.propTypes = {
  student: PropTypes.object.isRequired,
  booking: PropTypes.object,
  onBookingCreated: PropTypes.func,
};

export default StepNewBooking;
